import { useRouter } from "next/router";
import { staticRequest } from "tinacms";
import { useTina } from "tinacms/dist/react";
import { TinaMarkdown } from "tinacms/dist/rich-text";

import {
  I18n_FrConnection,
  Page,
  PostConnection,
} from "../.tina/__generated__/types";
import { BoxSection } from "../components/Box";
import Category from "../components/Category";
import { Layout } from "../components/layout/Layout";
import { getCategoriesPageJsonLd } from "../components/layout/head/getCategoriesPageJsonLd";
import { getTranslatedPosts } from "../src/getTranslatedPosts";
import { getListGql } from "../src/gql/getGql";
import { getSortedListablePosts } from "../src/utils";

/* eslint-disable prettier/prettier */

const getQuery = (locale: string, isLive?: boolean) => ({ query: `
  query getCategories($relativePath: String!) {
    page(relativePath: $relativePath) {
      body
    }
    ${getListGql(isLive)}
  }`,
variables: {
  relativePath: `${locale === "fr" ? "fr/" : ""}categories.mdx`,
}
});

/* eslint-enable prettier/prettier */

const description = "This page lists posts in each category.";

type DataType = {
  page: Page;
  postConnection: PostConnection;
  i18n_frConnection: I18n_FrConnection;
};

export default function CategoriesPage(props: {
  data: DataType;
  query: string;
}) {
  // Pass our data through the "useTina" hook to make it editable
  const { data } = useTina({
    query: props.query,
    variables: {},
    data: props.data,
  });
  const { locale } = useRouter();

  const page = data.page.body;

  const enEdges = data.postConnection.edges;
  const frEdges = data.i18n_frConnection?.edges;

  const translatedPosts = getTranslatedPosts(enEdges, frEdges, locale);
  const sortedPosts = getSortedListablePosts(translatedPosts);

  const categories = {};

  for (const post of sortedPosts) {
    const nodeCategories = post.categories;
    for (const category of nodeCategories) {
      categories[category] = [post, ...(categories[category] ?? [])];
    }
  }

  return (
    <Layout
      title="Categories"
      description={description}
      jsonLd={getCategoriesPageJsonLd({ locale })}
    >
      <article>
        <BoxSection>
          <TinaMarkdown content={page} />
          {Object.entries(categories).map(([name, category]: [string, any]) => (
            <Category categoryName={name} posts={category} key={name} />
          ))}
        </BoxSection>
      </article>
    </Layout>
  );
}

export const getStaticProps = async (ctx) => {
  const query = getQuery(ctx.locale, false);
  const data = await staticRequest({ ...query });

  return {
    props: {
      query: getQuery(ctx.locale, true),
      variables: {},
      data,
    },
  };
};
